import type React from 'react';
import { useSelector } from 'react-redux';
import type { RootReduxState } from 'app/shared/flux/reducers/index';
import { css } from '@/styled-system/css';
import Card from '../../../ui/Card';
import Carousel from '../../../ui/Carousel';
import { Badge } from '../../../ui/Badge';
import { KeyValue } from '../../../ui/KeyValue';
import { ReadMoreButton } from '../../../ui/ReadMoreButton';
import IconPetCat from 'images/icons/cat.svg';
import IconPetDog from 'images/icons/dog.svg';
import {
  additionalInfo,
  additionalInfoSection,
  infoContent,
  infoLabel,
  petCard,
  petCardItem,
  petCardItemSingle,
  petDetails,
  petIcon,
} from './petsModule.styles';

type PetPolicy = {
  allowed: boolean;
  deposit: string | null;
  oneTimeFee: string | null;
  monthlyFee: string | null;
  maxNumberAllowed: string | null;
  maxWeightAllowed: string | null;
  comment: string;
  restriction: string;
};

type PetPolicyMap = {
  dog?: PetPolicy;
  cat?: PetPolicy;
};

/**
 * Helper function to extract monetary amount from formatted string
 * Example: "$300 one time fee" -> "$300"
 */
const extractFeeAmount = (feeString: string | null): string | null => {
  if (!feeString) return null;

  const match = feeString.match(/^\$\d+(?:,\d{3})*(?:\.\d{2})?/);
  return match ? match[0] : null;
};

/**
 * Helper function to extract number from formatted string
 * Example: "2 pets max" -> "2"
 */
const extractNumberAllowed = (numberString: string | null): string | null => {
  if (!numberString) return null;

  const match = numberString.match(/^(\d+)/);
  return match ? match[1] : null;
};

/**
 * Helper function to extract weight limit from formatted string
 * Example: "50 lbs max" -> "50 lbs"
 */
const extractWeightLimit = (weightString: string | null): string | null => {
  if (!weightString) return null;

  const match = weightString.match(/^(\d+\s*lbs?)/);
  return match ? match[1] : null;
};

type PetCardProps = {
  petType: 'dog' | 'cat';
  policy: PetPolicy;
};

const isOnlyPetTypeName = (comment: string, petType: 'dog' | 'cat'): boolean => {
  const normalized = comment.trim().toLowerCase();
  return normalized === petType || normalized === `${petType}s`;
};

const PetCard: React.FC<PetCardProps> = ({ petType, policy }) => {
  const petName = petType === 'dog' ? 'Dogs' : 'Cats';
  const petIconSrc = petType === 'dog' ? IconPetDog : IconPetCat;
  const badgeTone = policy.allowed ? 'primary' : 'alert';
  const badgeText = policy.allowed ? 'Allowed' : 'Not allowed';
  const hasComment = Boolean(policy.comment) && !isOnlyPetTypeName(policy.comment, petType);

  return (
    <Card variant="default" size="sm" className={petCard}>
      <Card.Header layout="space-between">
        <div className={css({ display: 'flex', alignItems: 'center', gap: '1x' })}>
          <Card.Icon>
            <img src={petIconSrc} alt="" aria-hidden="true" className={petIcon} />
          </Card.Icon>
          <Card.Title size="lg">{petName}</Card.Title>
        </div>
        <Badge tone={badgeTone} size="md">
          {badgeText}
        </Badge>
      </Card.Header>

      {policy.allowed && (
        <Card.Body>
          <div className={petDetails}>
            {/* Deposit */}
            {policy.deposit && <KeyValue label="Deposit" value={extractFeeAmount(policy.deposit)} />}

            {/* One-time fee */}
            {policy.oneTimeFee && <KeyValue label="One-time fee" value={extractFeeAmount(policy.oneTimeFee)} />}

            {/* Monthly fee */}
            {policy.monthlyFee && <KeyValue label="Monthly fee" value={`${extractFeeAmount(policy.monthlyFee)}/mo`} />}

            {/* Number allowed */}
            {policy.maxNumberAllowed && (
              <KeyValue label="Number allowed" value={extractNumberAllowed(policy.maxNumberAllowed)} />
            )}

            {/* Weight limit */}
            {policy.maxWeightAllowed && (
              <KeyValue label="Weight limit" value={extractWeightLimit(policy.maxWeightAllowed)} />
            )}

            {/* Additional information section */}
            {(policy.restriction || hasComment) && (
              <div className={additionalInfo}>
                {policy.restriction && (
                  <section aria-labelledby={`${petType}-restrictions-heading`}>
                    <h4 className={infoLabel} id={`${petType}-restrictions-heading`}>
                      Restrictions
                    </h4>
                    <div className={infoContent}>
                      <ReadMoreButton
                        content={policy.restriction}
                        maxTruncatedLength={100}
                        readMoreText="Read more"
                        readLessText="Read less"
                        aria-label="Pet policy restrictions"
                      />
                    </div>
                  </section>
                )}

                {hasComment && (
                  <section
                    aria-labelledby={`${petType}-additional-info-heading`}
                    className={policy.restriction ? additionalInfoSection : undefined}
                  >
                    <h4 className={infoLabel} id={`${petType}-additional-info-heading`}>
                      Additional Information
                    </h4>
                    <div className={infoContent}>
                      <ReadMoreButton
                        content={policy.comment}
                        maxTruncatedLength={100}
                        readMoreText="Read more"
                        readLessText="Read less"
                        aria-label="Pet policy additional information"
                      />
                    </div>
                  </section>
                )}
              </div>
            )}
          </div>
        </Card.Body>
      )}
    </Card>
  );
};

/**
 * PetsModule Component
 *
 * Displays pet policy information for dogs and cats using real listing data from Redux.
 * Shows allowance status, fees, restrictions, and other pet-related information
 * in a horizontal scrolling card layout.
 *
 * Note: This component returns content directly without Module wrapper.
 * The ModuleRenderer handles wrapping this in a Module with title "Pets".
 *
 * Features:
 * - Fetches real pet policy data from Redux state
 * - Horizontal scrolling layout for pet cards
 * - Status badges (primary for allowed, alert for not allowed)
 * - Fee information extraction and formatting
 * - Conditional rendering for missing data
 * - Accessibility support with proper semantics
 */
export const PetsModule: React.FC = () => {
  // Fetch pet policy data from Redux state
  const petPolicies = useSelector(
    (state: RootReduxState) => state.currentListingDetails?.currentListing?.amenities?.petPolicies,
  ) as PetPolicyMap | null;

  // Hide entirely when there is no pet data (shouldRender in moduleConfig is the primary gate)
  if (!petPolicies || (!petPolicies.dog && !petPolicies.cat)) {
    return null;
  }

  const hasBoth = Boolean(petPolicies.dog) && Boolean(petPolicies.cat);
  const itemClassName = hasBoth ? petCardItem : petCardItemSingle;

  return (
    <Carousel gap="md" snapAlign="start" aria-label="Pet policy information">
      {/* Dog policy card */}
      {petPolicies.dog && (
        <Carousel.Item className={itemClassName}>
          <PetCard petType="dog" policy={petPolicies.dog} />
        </Carousel.Item>
      )}

      {/* Cat policy card */}
      {petPolicies.cat && (
        <Carousel.Item className={itemClassName}>
          <PetCard petType="cat" policy={petPolicies.cat} />
        </Carousel.Item>
      )}
    </Carousel>
  );
};
